import React, { useState, useEffect, useCallback } from 'react';
import styles from './AdminDashboard.module.css';
import { Button } from '../../components/common/Button/Button';
import axios from 'axios';

interface DataSource {
  id: string;
  name: string;
  category: string;
  description: string;
  provider: string;
  type: string;
  status: 'online' | 'offline' | 'timeout' | 'connecting';
  statusCode?: number;
  latency?: number;
  error?: string;
}

interface DataSourcesResponse {
  checkedAt: string;
  sources: DataSource[];
}

const categoryOrder = [
  'Interne API\'s',
  'Kaartlagen',
  'Geocoding',
  'Demografie',
  'Gebouwen',
  'Voorzieningen',
  'Omgevingsdata',
  'Database',
];

const CategoryIcon: React.FC<{ category: string }> = ({ category }) => {
  const icons: Record<string, React.ReactNode> = {
    'Interne API\'s': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
        <line x1="6" y1="6" x2="6.01" y2="6" />
        <line x1="6" y1="18" x2="6.01" y2="18" />
      </svg>
    ),
    'Kaartlagen': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    ),
    'Geocoding': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    'Demografie': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    'Gebouwen': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path d="M9 22v-4h6v4" />
        <path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01" />
      </svg>
    ),
    'Voorzieningen': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    ),
    'Omgevingsdata': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    'Database': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </svg>
    ),
  };
  return <span style={{ display: 'inline-flex', color: '#666D70' }}>{icons[category] || icons['Database']}</span>;
};

export const DataSourcesPage: React.FC = () => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<DataSourcesResponse>('/datasources');
      setSources(response.data.sources);
      setCheckedAt(response.data.checkedAt);
    } catch (err) {
      console.error('Failed to fetch data sources:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const grouped = categoryOrder.reduce<Record<string, DataSource[]>>((acc, cat) => {
    const items = sources.filter((s) => s.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const onlineCount = sources.filter((s) => s.status === 'online').length;
  const totalCount = sources.length;

  const getStatusBadge = (source: DataSource) => {
    const statusStyles: Record<string, React.CSSProperties> = {
      online: { background: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' },
      offline: { background: 'rgba(244, 67, 54, 0.1)', color: '#F44336' },
      timeout: { background: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' },
      connecting: { background: 'rgba(39, 124, 234, 0.1)', color: '#277CEA' },
    };

    const labels: Record<string, string> = {
      online: 'Online',
      offline: 'Offline',
      timeout: 'Timeout',
      connecting: 'Verbinden...',
    };

    return (
      <span
        className={styles.statusBadge}
        style={statusStyles[source.status] || statusStyles.offline}
      >
        {labels[source.status] || source.status}
      </span>
    );
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <div>
          <h2 style={{ margin: 0 }}>Databronnen</h2>
          {checkedAt && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#9DA6A8' }}>
              Laatst gecheckt: {new Date(checkedAt).toLocaleString('nl-NL')}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {totalCount > 0 && (
            <span style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: onlineCount === totalCount ? '#4CAF50' : '#FF9800',
            }}>
              {onlineCount}/{totalCount} online
            </span>
          )}
          <Button variant="primary" onClick={fetchStatus} disabled={loading}>
            {loading ? 'Checken...' : 'Status vernieuwen'}
          </Button>
        </div>
      </div>

      {loading && sources.length === 0 ? (
        <div className={styles.loading}>
          API's worden gecheckt...
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.dataTable} style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '35%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Naam</th>
                <th>Type</th>
                <th>Provider</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Latency</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([category, items]) => (
                <React.Fragment key={category}>
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        padding: '0.75rem 1rem 0.5rem',
                        borderBottom: 'none',
                        background: '#FAFAFA',
                      }}
                    >
                      <span style={{
                        fontFamily: "'Avenir Next', sans-serif",
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: '#666D70',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}>
                        <CategoryIcon category={category} />
                        {category}
                      </span>
                    </td>
                  </tr>
                  {items.map((source) => (
                    <tr key={source.id}>
                      <td>
                        <div>
                          <strong style={{ color: '#323232' }}>{source.name}</strong>
                          <div style={{ fontSize: '0.85rem', color: '#9DA6A8', marginTop: '0.15rem' }}>
                            {source.description}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          background: '#F5F5F5',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          color: '#666D70',
                          whiteSpace: 'nowrap',
                        }}>
                          {source.type}
                        </span>
                      </td>
                      <td style={{ color: '#666D70' }}>{source.provider}</td>
                      <td>{getStatusBadge(source)}</td>
                      <td style={{ color: '#666D70', fontFamily: 'monospace', fontSize: '0.9rem', textAlign: 'right' }}>
                        {source.latency != null && source.latency > 0
                          ? `${source.latency}ms`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
